import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  makeTranslator, setOnboardingLanguage, SUPPORTED_LOCALES,
} from "@/lib/onboarding_i18n";

// Slice 2D1 · English / Español language foundation.
//
// Single reactive source of truth for the application's active
// language. The `lang` React state below is authoritative — the
// module-level fallback in `onboarding_i18n.js` is kept in sync via
// `setOnboardingLanguage(lang)` on every render solely so any code
// path that still uses the stateless module `t` observes the current
// value. Consumers must read the language through `useLang()` and
// call the bound `t` returned by the context so React re-renders when
// the contractor flips the toggle.
//
// Persistence order of authority:
//   1. `user.app_language` from the backend (source of truth for the
//      contractor's durable preference; hydrated once from the fresh
//      `/auth/me` response into React state on first authenticated
//      mount when the local cache is absent).
//   2. `localStorage["jc_lang"]` — a UX-only cache that lets the
//      first paint use the correct language on hard refresh (before
//      React state or `/auth/me` have a chance to resolve).
//   3. Default "en".

const LangContext = createContext(null);

const _readLocalCache = () => {
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem("jc_lang");
    return SUPPORTED_LOCALES.includes(v) ? v : null;
  } catch (_) {
    return null;
  }
};

const _sanitize = (v) => (SUPPORTED_LOCALES.includes(v) ? v : "en");

export const LangProvider = ({ children, user }) => {
  // Initial state comes from the local cache so the very first React
  // commit renders the correct language on hard refresh.
  const [lang, setLangState] = useState(() => _readLocalCache() || "en");

  // Keep the stateless module fallback in lockstep with React state.
  // Runs synchronously on every render BEFORE children commit so any
  // legacy consumer of the module `t` observes the current locale
  // without a race. (`setOnboardingLanguage` is idempotent and
  // side-effect-only on a module variable — safe to call in render.)
  setOnboardingLanguage(lang);

  // Hydrate ONLY when no valid local cache exists (first-ever login
  // on this browser). If the contractor has already saved a choice
  // locally, the local cache — which reflects their most recent
  // toggle — wins over any cached-user-record synthesized default.
  useEffect(() => {
    if (!user) return;
    if (_readLocalCache()) return;
    const server = _sanitize(user.app_language);
    setLangState((prev) => (prev === server ? prev : server));
    try { localStorage.setItem("jc_lang", server); } catch (_) {}
  }, [user]);

  // Explicit setter. Consumers should call `setLang("en")` or
  // `setLang("es")` directly — never derive next value from stale
  // state. The click handlers in the segmented toggle pass the exact
  // desired locale so a single click always lands on the requested
  // language even if the previous state has not yet flushed.
  const setLang = async (next) => {
    const clean = _sanitize(next);
    setLangState(clean);
    try { localStorage.setItem("jc_lang", clean); } catch (_) {}
    setOnboardingLanguage(clean);
    if (user) {
      try {
        await api.put("/auth/language", { app_language: clean });
      } catch (_) { /* silent — next `/auth/me` reconciles */ }
    }
  };

  // Bound translator is a fresh reference on every locale change so
  // any component consuming `useLang().t` re-renders when the
  // contractor toggles. Memoize per-lang so identical renders reuse
  // the same function.
  const t = useMemo(() => makeTranslator(lang), [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, user, t],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  // Safety net for any component rendered outside LangProvider (e.g.
  // pre-auth marketing routes). Return an EN-locked shim so callers
  // never crash and never accidentally opt into Spanish without a
  // provider.
  if (!ctx) {
    return { lang: "en", setLang: () => {}, t: makeTranslator("en") };
  }
  return ctx;
};
